import { useEffect } from 'react';
import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import type { Field } from '../../utils';
import type { IValidator } from '../../types';
import { FieldError } from '../field-error/field-error.component';
import { Text } from '../text/text.component';
import { mergeStyle, type Style } from './text-field.module.css';

interface Props {
  label?: string;
  value?: string;
  formField?: Field<string>;
  placeholder?: string;
  error?: string;
  hint?: string;
  type?: string;
  autofocus?: boolean;
  disabled?: boolean;
  validators?: IValidator[];
  style?: Style;
  rightIcon?: ReactNode;
  onClick?: () => void;
  onChange?: (value: string) => void;
  onEnter?: () => void;
  onFocus?: () => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

export function TextField(props: Props) {

  const { 
    label, 
    value: initValue, 
    formField: field,
    placeholder,
    hint,
    error,
    type,
    autofocus,
    disabled = false,
    validators = [],
    style: customStyle,
    rightIcon,
    onClick,
    onChange,
    onEnter,
    onFocus,
    onBlur
  } = props;
  
  const style = mergeStyle(customStyle);
  const value = field ? field.value : initValue;

  useEffect(() => {
    field?.addValidators(validators);
  }, [JSON.stringify(validators)]);

  useEffect(() => {
    return () => field?.addValidators([]);
  }, []);

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    event.target.focus();
    onFocus?.();
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    event.target.blur();
    field?.validate();
    onBlur?.(event);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    field?.update(event.target.value);
    if (onChange) onChange(event.target.value);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      onEnter?.();
    }
  }

  return (
    <div 
      className={style.container}
      onClick={onClick}>
      {label && 
        <label className={style.label}>{label}</label>
      }
      <div className={style.inputContainer}>
        <input 
          className={style.input} 
          value={value}
          type={type}
          placeholder={placeholder}
          autoFocus={autofocus}
          disabled={disabled}
          onWheel={e => type === 'number' && e.currentTarget.blur()}
          onChange={handleChange} 
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}/>
        {rightIcon &&
          <div className={style.iconContainer}>
            {rightIcon}
          </div>
        }
      </div>
      <FieldError className={style.error} message={field?.error ?? error}/>
      {hint && <Text className={style.hint} text={hint}/>}
    </div>
  );
}
